import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdCampaign,
  MdWork,
  MdCheckCircle,
  MdPendingActions,
  MdAccountBalanceWallet,
  MdTrendingUp,
  MdPayments,
  MdSchedule,
  MdRocketLaunch,
  MdShowChart,
} from "react-icons/md";
import { themes } from "../../theme.ts";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { influencerCampaingnsRequest, fetchUserWallet } from "../../services/Common.ts";
import { getAllCampaigns } from "../../services/Campaigns.ts";

interface Activity {
  id: number;
  type: string;
  campaign: string;
  status: string;
  date: string;
}

interface WalletData {
  id: string;
  user: number;
  total_balance: number;
  total_withdraw: number;
  pending_amount: number;
  total_this_month: number;
}

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { themeName } = useTheme();
  const t = (themes as any)[themeName].colors as Record<string, string>;

  const [stats, setStats] = useState({
    appliedCampaigns: 0,
    activeRequests: 0,
    completedCampaigns: 0,
    pendingRequests: 0,
    totalEarnings: 0,
    allCampaigns: 0,
  });

  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [recentActivity, setRecentActivity] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  const userData = localStorage.getItem("user");
  const user = userData ? JSON.parse(userData) : null;
  const accessToken = user?.access;
  const userId = user?.user?.id;

  // --- STATS CALC ---
  const calculateStatsFromRequests = (requests: any[]) => {
    if (!requests || !Array.isArray(requests)) {
      return {
        appliedCampaigns: 0,
        activeRequests: 0,
        completedCampaigns: 0,
        pendingRequests: 0,
        totalEarnings: 0,
      };
    }

    const appliedCampaigns = requests.length;
    const activeRequests = requests.filter(
      (r) => r.status === "APPROVED" || r.status === "IN_PROGRESS"
    ).length;
    const completedCampaigns = requests.filter(
      (r) => r.status === "COMPLETED"
    ).length;
    const pendingRequests = requests.filter((r) => r.status === "PENDING")
      .length;

    const totalEarnings = requests
      .filter((r) => r.status === "COMPLETED")
      .reduce(
        (total, r) =>
          total + (r.campaign?.reward_amount || r.reward_amount || 0),
        0
      );

    return {
      appliedCampaigns,
      activeRequests,
      completedCampaigns,
      pendingRequests,
      totalEarnings,
    };
  };

  // --- ACTIVITY FORMAT ---
  const getActivityType = (status: string) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "application";
      case "APPROVED":
        return "approval";
      case "COMPLETED":
        return "completion";
      default:
        return "application";
    }
  };

  const formatRecentActivity = (requests: any[]) => {
    if (!requests || !Array.isArray(requests)) return [];
    return requests.slice(0, 5).map((r) => ({
      id: r.id,
      type: getActivityType(r.status),
      campaign: r.campaign?.title || r.campaign_name || "Unknown Campaign",
      status: r.status?.toLowerCase(),
      date:
        r.created_at || r.applied_date || new Date().toISOString().split("T")[0],
    }));
  };

  // --- DATA FETCH ---
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);

        if (!accessToken || !userId) {
          setLoading(false);
          return;
        }

        let allCampaignsCount = 0;
        let userRequests = [];

        // Fetch campaigns
        const campaignsResponse = await getAllCampaigns(accessToken);
        if (campaignsResponse?.data)
          allCampaignsCount = campaignsResponse.data.length || 0;

        // Fetch user campaign requests
        const requestsResponse = await influencerCampaingnsRequest(
          accessToken,
          userId
        );
        if (requestsResponse?.data) {
          userRequests = requestsResponse.data;
          const statsCalc = calculateStatsFromRequests(userRequests);
          setStats({ ...statsCalc, allCampaigns: allCampaignsCount });
          setRecentActivity(formatRecentActivity(userRequests));
        }

        // Fetch wallet data
        const walletResponse = await fetchUserWallet(accessToken, userId);
        if (walletResponse?.data) {
          setWallet(walletResponse.data);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, [accessToken, userId]);

  // --- CHARTS DATA (COMMENTED OUT FOR FUTURE USE) ---
  /*
  const userActivityData = [
    { name: "Available", value: stats.allCampaigns },
    { name: "Applied", value: stats.appliedCampaigns },
    { name: "Active", value: stats.activeRequests },
    { name: "Completed", value: stats.completedCampaigns },
    { name: "Pending", value: stats.pendingRequests },
  ];

  const pieChartData = [
    { name: "Available", value: stats.allCampaigns },
    { name: "Applied", value: stats.appliedCampaigns },
    { name: "Active", value: stats.activeRequests },
    { name: "Completed", value: stats.completedCampaigns },
    { name: "Pending", value: stats.pendingRequests },
  ];

  const COLORS = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#FFD166", "#9D4EDD"];
  */

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "application":
        return "📝";
      case "approval":
        return "✅";
      case "completion":
        return "🎉";
      default:
        return "📋";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return t.success ?? "#34D399";
      case "completed":
        return t.primary ?? "#14B8A6";
      case "pending":
        return t.warning ?? "#FBBF24";
      default:
        return t.textMuted ?? "#718096";
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${t.background} 0%, ${t.surface} 100%)`,
        }}
      >
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-16 w-16 mx-auto mb-4 border-4"
            style={{
              borderColor: `${t.primary}20`,
              borderTopColor: t.primary,
            }}
          />
          <p style={{ color: t.textSecondary }}>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // --- MAIN RETURN ---
  return (
    <div
      className="min-h-screen"
      style={{
        background: `linear-gradient(135deg, ${t.background} 0%, ${t.surface} 100%)`,
      }}
    >
      <div className="container mx-auto px-4 py-6">
        
        {/* ===== HEADER SECTION ===== */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" 
                  style={{ background: `${t.primary}20`, color: t.primary }}>
                  <MdRocketLaunch size={20} />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold" style={{ color: t.text }}>
                  {`Welcome back, ${user?.user?.profile?.first_name || user?.user?.email}!`}
                </h1>
              </div>
              <p className="text-lg opacity-80" style={{ color: t.text }}>
                Track your campaign progress and earnings in one place
              </p>
            </div>
            
            {/* Main Wallet Card */}
            {wallet && (
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-6 text-white shadow-xl min-w-[280px]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white">Total Balance</h3>
                  <MdAccountBalanceWallet size={24} className="text-white" />
                </div>
                <p className="text-3xl font-bold mb-2">₹{wallet.total_balance}</p>
                <p className="text-blue-100 text-sm">Available for withdrawal</p>
              </div>
            )}
          </div>

          {/* Quick Stats Bar */}
          {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 rounded-xl border" 
              style={{ borderColor: t.border, background: t.surface }}>
              <p className="text-2xl font-bold mb-1" style={{ color: t.text }}>
                {stats.allCampaigns}
              </p>
              <p className="text-sm" style={{ color: t.textSecondary }}>Available Campaigns</p>
            </div>
            <div className="text-center p-4 rounded-xl border" 
              style={{ borderColor: t.border, background: t.surface }}>
              <p className="text-2xl font-bold mb-1" style={{ color: t.text }}>
                {stats.completedCampaigns}
              </p>
              <p className="text-sm" style={{ color: t.textSecondary }}>Completed</p>
            </div>
            <div className="text-center p-4 rounded-xl border" 
              style={{ borderColor: t.border, background: t.surface }}>
              <p className="text-2xl font-bold mb-1" style={{ color: t.text }}>
                {stats.activeRequests}
              </p>
              <p className="text-sm" style={{ color: t.textSecondary }}>In Progress</p>
            </div>
            <div className="text-center p-4 rounded-xl border" 
              style={{ borderColor: t.border, background: t.surface }}>
              <p className="text-2xl font-bold mb-1" style={{ color: t.text }}>
                ₹{stats.totalEarnings}
              </p>
              <p className="text-sm" style={{ color: t.textSecondary }}>Total Earned</p>
            </div>
          </div> */}
        </div>

        {/* ===== MAIN DASHBOARD GRID ===== */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          
          {/* ===== LEFT COLUMN: CAMPAIGN & WALLET DETAILS ===== */}
          <div className="xl:col-span-2 space-y-8">
            
            {/* 🔥 CAMPAIGN PERFORMANCE SECTION */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" 
                  style={{ background: `${t.primary}20`, color: t.primary }}>
                  <MdShowChart size={18} />
                </div>
                <h2 className="text-xl font-bold" style={{ color: t.text }}>Campaign Performance</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  {
                    label: "Applied Campaigns",
                    value: stats.appliedCampaigns,
                    icon: MdCampaign,
                    color: t.secondary,
                    description: "Campaigns you've applied to",
                    trend: "Total applications"
                  },
                  {
                    label: "Active Requests",
                    value: stats.activeRequests,
                    icon: MdWork,
                    color: t.success,
                    description: "Currently working on",
                    trend: "In progress"
                  },
                  {
                    label: "Completed Campaigns",
                    value: stats.completedCampaigns,
                    icon: MdCheckCircle,
                    color: t.primary,
                    description: "Successfully delivered",
                    trend: "Finished work"
                  },
                  {
                    label: "Pending Approval",
                    value: stats.pendingRequests,
                    icon: MdPendingActions,
                    color: t.warning,
                    description: "Waiting for approval",
                    trend: "Under review"
                  },
                ].map((s, i) => (
                  <div
                    key={i}
                    className="rounded-2xl p-6 border transition-all duration-300 hover:shadow-lg group"
                    style={{
                      background: t.surface,
                      border: `1px solid ${t.border}`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-3xl font-bold mb-2 group-hover:scale-105 transition-transform" 
                          style={{ color: t.text }}>
                          {s.value}
                        </p>
                        <p className="font-semibold mb-1" style={{ color: t.text }}>
                          {s.label}
                        </p>
                      </div>
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                        style={{ background: `${s.color}15` }}
                      >
                        <s.icon size={28} color={s.color} />
                      </div>
                    </div>
                    <p className="text-sm mb-2" style={{ color: t.textSecondary }}>
                      {s.description}
                    </p>
                    <p className="text-xs font-medium" style={{ color: s.color }}>
                      {s.trend}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* 💰 WALLET BREAKDOWN SECTION */}
            {wallet && (
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" 
                    style={{ background: `${t.success}20`, color: t.success }}>
                    <MdAccountBalanceWallet size={18} />
                  </div>
                  <h2 className="text-xl font-bold" style={{ color: t.text }}>Wallet Breakdown</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {
                      label: "Available Balance",
                      value: `₹${wallet.total_balance}`,
                      icon: MdAccountBalanceWallet,
                      color: t.primary,
                      description: "Ready to withdraw instantly",
                      subtitle: "Current balance"
                    },
                    {
                      label: "Total Withdrawn",
                      value: `₹${wallet.total_withdraw}`,
                      icon: MdPayments,
                      color: t.success,
                      description: "Amount withdrawn to date",
                      subtitle: "All time withdrawals"
                    },
                    {
                      label: "Pending Clearance",
                      value: `₹${wallet.pending_amount}`,
                      icon: MdSchedule,
                      color: t.warning,
                      description: "Awaiting payment clearance",
                      subtitle: "In processing"
                    },
                    {
                      label: "This Month Earnings",
                      value: `₹${wallet.total_this_month}`,
                      icon: MdTrendingUp,
                      color: t.secondary,
                      description: "Earned in current month",
                      subtitle: "Monthly income"
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="rounded-2xl p-6 border transition-all duration-300 hover:shadow-lg group"
                      style={{
                        background: t.surface,
                        border: `1px solid ${t.border}`,
                      }}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-2xl font-bold mb-2 group-hover:scale-105 transition-transform" 
                            style={{ color: t.text }}>
                            {item.value}
                          </p>
                          <p className="font-semibold mb-1" style={{ color: t.text }}>
                            {item.label}
                          </p>
                        </div>
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                          style={{ background: `${item.color}15` }}
                        >
                          <item.icon size={24} color={item.color} />
                        </div>
                      </div>
                      <p className="text-sm mb-2" style={{ color: t.textSecondary }}>
                        {item.description}
                      </p>
                      <p className="text-xs font-medium" style={{ color: item.color }}>
                        {item.subtitle}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 📊 CHARTS SECTION - COMMENTED OUT FOR FUTURE USE */}
            {/*
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" 
                  style={{ background: `${t.info}20`, color: t.info }}>
                  <MdBarChart size={18} />
                </div>
                <h2 className="text-xl font-bold" style={{ color: t.text }}>Performance Analytics</h2>
              </div>

              <div className="grid grid-cols-1 gap-8">
                <div
                  className="rounded-3xl p-6 border"
                  style={{
                    background: t.surface,
                    border: `1px solid ${t.border}`,
                  }}
                >
                  <h3 className="text-lg font-semibold mb-4" style={{ color: t.text }}>
                    Campaign Activity Overview
                  </h3>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={userActivityData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.border} />
                        <XAxis dataKey="name" tick={{ fill: t.textSecondary }} />
                        <YAxis tick={{ fill: t.textSecondary }} />
                        <Tooltip />
                        <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={40}>
                          {userActivityData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div
                  className="rounded-3xl p-6 border"
                  style={{
                    background: t.surface,
                    border: `1px solid ${t.border}`,
                  }}
                >
                  <h3 className="text-lg font-semibold mb-4" style={{ color: t.text }}>
                    Campaign Distribution
                  </h3>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={100}
                          innerRadius={60}
                          fill={t.secondary}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </section>
            */}
          </div>

          {/* ===== RIGHT COLUMN: RECENT ACTIVITY & QUICK ACTIONS ===== */}
          <div className="xl:col-span-1 space-y-8">
            
            {/* 📝 RECENT ACTIVITY SECTION */}
            <section>
              <div
                className="rounded-2xl p-6 border h-full"
                style={{
                  background: t.surface,
                  border: `1px solid ${t.border}`,
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" 
                      style={{ background: `${t.warning}20`, color: t.warning }}>
                      <MdCampaign size={18} />
                    </div>
                    <h2 className="text-xl font-bold" style={{ color: t.text }}>
                      Recent Activity
                    </h2>
                  </div>
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{
                      background: `${t.primary}15`,
                      color: t.primary,
                    }}
                  >
                    {recentActivity.length}
                  </span>
                </div>
                
                <div className="space-y-4">
                  {recentActivity.length > 0 ? (
                    recentActivity.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-start p-4 rounded-xl border transition-all duration-300 hover:shadow-md group"
                        style={{
                          background: themeName === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                          border: `1px solid ${t.border}`,
                        }}
                      >
                        <span className="text-2xl mr-3 mt-1 group-hover:scale-110 transition-transform">
                          {getActivityIcon(a.type)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate mb-1" style={{ color: t.text }}>
                            {a.campaign}
                          </p>
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs px-2 py-1 rounded-full capitalize font-medium"
                              style={{
                                background: getStatusColor(a.status) + '15',
                                color: getStatusColor(a.status),
                              }}
                            >
                              {a.status}
                            </span>
                            <span className="text-xs" style={{ color: t.textSecondary }}>
                              {a.type}
                            </span>
                          </div>
                        </div>
                        <span
                          className="text-xs px-2 py-1 rounded-full whitespace-nowrap ml-2"
                          style={{
                            background: themeName === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                            color: t.textSecondary,
                          }}
                        >
                          {new Date(a.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-3 opacity-50">📊</div>
                      <p className="font-medium mb-1" style={{ color: t.text }}>
                        No activity yet
                      </p>
                      <p className="text-sm" style={{ color: t.textSecondary }}>
                        Your campaign activities will appear here
                      </p>
                    </div>
                  )}
                </div>

                {recentActivity.length > 0 && (
                  <button
                    className="w-full mt-6 py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-md border"
                    style={{
                      background: 'transparent',
                      color: t.primary,
                      borderColor: t.primary,
                    }}
                    onClick={() => navigate('/dashboard/notifications')}
                  >
                    View All Notifications
                  </button>
                )}
              </div>
            </section>

            {/* 🚀 QUICK ACTIONS SECTION */}
            <section>
              <div
                className="rounded-2xl p-6 border"
                style={{
                  background: t.surface,
                  border: `1px solid ${t.border}`,
                }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" 
                    style={{ background: `${t.success}20`, color: t.success }}>
                    <MdRocketLaunch size={18} />
                  </div>
                  <h2 className="text-xl font-bold" style={{ color: t.text }}>Quick Actions</h2>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/campaigns')}
                    className="w-full p-4 rounded-xl border text-left transition-all duration-300 hover:shadow-md group"
                    style={{
                      background: themeName === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                      borderColor: t.border,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold" style={{ color: t.text }}>Browse Campaigns</p>
                        <p className="text-sm" style={{ color: t.textSecondary }}>Find new opportunities</p>
                      </div>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform"
                        style={{ background: `${t.primary}15` }}>
                        <MdCampaign size={20} color={t.primary} />
                      </div>
                    </div>
                  </button>

                  {wallet && wallet.total_balance > 0 && (
                    <button
                      onClick={() => navigate('/wallet')}
                      className="w-full p-4 rounded-xl border text-left transition-all duration-300 hover:shadow-md group"
                      style={{
                        background: themeName === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                        borderColor: t.border,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold" style={{ color: t.text }}>Withdraw Funds</p>
                          <p className="text-sm" style={{ color: t.textSecondary }}>Transfer to your bank</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform"
                          style={{ background: `${t.success}15` }}>
                          <MdPayments size={20} color={t.success} />
                        </div>
                      </div>
                    </button>
                  )}

                  <button
                    onClick={() => navigate('/profile')}
                    className="w-full p-4 rounded-xl border text-left transition-all duration-300 hover:shadow-md group"
                    style={{
                      background: themeName === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                      borderColor: t.border,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold" style={{ color: t.text }}>Update Profile</p>
                        <p className="text-sm" style={{ color: t.textSecondary }}>Improve approval chances</p>
                      </div>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform"
                        style={{ background: `${t.secondary}15` }}>
                        <MdWork size={20} color={t.secondary} />
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;